import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, MapPin, Trash2, Building2 } from 'lucide-react';

export default function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '', manager_id: '' });

  const fetchLocations = useCallback(async () => {
    try { const { data } = await api.get('/locations'); setLocations(data); } catch { /* */ }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const resetForm = () => { setForm({ name: '', address: '', phone: '', manager_id: '' }); setEditLoc(null); };
  const openNew = () => { resetForm(); setFormOpen(true); };
  const openEdit = (loc) => { setForm({ name: loc.name, address: loc.address || '', phone: loc.phone || '', manager_id: loc.manager_id || '' }); setEditLoc(loc); setFormOpen(true); };

  const saveLoc = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editLoc) { await api.put(`/locations/${editLoc.id}`, form); toast.success('Location updated'); }
      else { await api.post('/locations', form); toast.success('Location created'); }
      setFormOpen(false); resetForm(); fetchLocations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteLoc = async (id) => {
    try { await api.delete(`/locations/${id}`); toast.success('Deleted'); fetchLocations(); } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider">{locations.length} locations</h3>
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogTrigger asChild>
            <Button size="sm" onClick={openNew} className="bg-gold text-black rounded-sm font-sans text-xs font-bold" data-testid="add-location-button"><Plus className="w-3 h-3 mr-1" /> Add Location</Button>
          </DialogTrigger>
          <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-white">{editLoc ? 'Edit' : 'New'} Location</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" placeholder="e.g. Majnu Melbourne CBD" data-testid="location-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Address</Label>
                <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="location-address-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Phone</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="location-phone-input" />
              </div>
              <Button onClick={saveLoc} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-location-button">{editLoc ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {locations.map(loc => (
          <div key={loc.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm card-hover" data-testid={`location-${loc.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-gold" />
                </div>
                <div>
                  <p className="text-white font-sans font-medium">{loc.name}</p>
                  {loc.address && <p className="text-zinc-400 text-xs font-sans flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {loc.address}</p>}
                  {loc.phone && <p className="text-zinc-500 text-xs font-sans mt-0.5">{loc.phone}</p>}
                  <Badge className="mt-2 bg-emerald-500/20 text-emerald-400 border-0 font-sans text-xs">{loc.status || 'active'}</Badge>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(loc)} className="h-7 text-zinc-400 hover:text-white font-sans text-xs" data-testid={`edit-location-${loc.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => deleteLoc(loc.id)} className="h-7 text-red-400 hover:text-red-300" data-testid={`delete-location-${loc.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        ))}
        {locations.length === 0 && <p className="text-zinc-500 font-sans text-center py-8 col-span-2" data-testid="no-locations">No locations yet</p>}
      </div>
    </div>
  );
}
